import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Icon } from "@core/icons";
import { ApiError } from "@core/http";
import { BigButton, Notice } from "../access/primitives";
import { loadValidatorSession } from "../access/session";
import { lookupCode } from "../home/api";
import type { Escaner } from "./port";
import { selectScanner } from "./selectScanner";
import "./scanner.css";

// SPEC-302 §5 — `validador.jsx:306-387` (`Escaner`), tres estados con
// baseline: `permiso` (RF-VAL-16), `buscando` (RF-VAL-18), `denegado`
// (RF-VAL-17). `escaner` es inyectable para test (puerto falso); en
// producción `selectScanner()` decide (ADR-012).
const MESSAGES = {
  qrInvalid: "Código no válido. Verifica que sea un código de Star Circle",
  noConnection: "Sin conexión. Revisa la señal o el wifi del local",
};

type Estado = "permiso" | "buscando" | "denegado";

const DENIED_STEPS = [
  "Toca el candado o el ícono ⚙ junto a la dirección web.",
  "Entra en «Permisos del sitio» y activa la Cámara.",
  "Vuelve aquí y toca «Reintentar».",
];

export function ScannerScreen({ escaner }: { escaner?: Escaner | null }) {
  const navigate = useNavigate();
  const videoRef = useRef<HTMLVideoElement>(null);
  const invalidTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const port = useMemo(
    () => (escaner === undefined ? selectScanner() : escaner),
    [escaner],
  );

  const [estado, setEstado] = useState<Estado>("permiso");
  const [invalidNotice, setInvalidNotice] = useState<string | null>(null);

  const bearerToken = loadValidatorSession()?.token ?? "";

  useEffect(() => {
    return () => {
      port?.detener();
      if (invalidTimeoutRef.current) clearTimeout(invalidTimeoutRef.current);
    };
  }, [port]);

  function goHome(openManual: boolean) {
    navigate(
      "/validator/home",
      openManual ? { state: { openManual: true } } : undefined,
    );
  }

  async function handleDetected(valor: string) {
    port?.detener();
    try {
      const data = await lookupCode(valor, "scan", bearerToken);
      navigate("/validator/confirm", { state: { code: data, entry: "scan" } });
    } catch (err) {
      if (err instanceof ApiError && err.code === "QR_INVALID") {
        showTransientNotice(MESSAGES.qrInvalid);
        void startCamera();
        return;
      }
      if (err instanceof ApiError) {
        // SPEC-304: 409/422 del lookup (`QR_EXPIRED`, `QR_USED`,
        // `MERCHANT_NOT_ENABLED`, `MEMBER_INACTIVE`, `REDEMPTION_EXHAUSTED`,
        // `INSUFFICIENT_BALANCE`) ya son un rechazo resuelto — se dibuja
        // directamente en `ResultScreen` (R-03), sin pasar por `Confirmar`.
        navigate("/validator/result", { state: { problem: err.problem } });
        return;
      }
      // Sin red en pleno escaneo: mismo tratamiento transitorio que
      // `QR_INVALID` — se avisa y se sigue leyendo, sin cerrar la pantalla.
      showTransientNotice(MESSAGES.noConnection);
      void startCamera();
    }
  }

  function showTransientNotice(message: string) {
    setInvalidNotice(message);
    if (invalidTimeoutRef.current) clearTimeout(invalidTimeoutRef.current);
    invalidTimeoutRef.current = setTimeout(() => {
      setInvalidNotice(null);
    }, 3000);
  }

  async function startCamera() {
    if (!port || !videoRef.current) {
      setEstado("denegado");
      return;
    }
    try {
      await port.iniciar(videoRef.current, (valor) => {
        void handleDetected(valor);
      });
      setEstado("buscando");
    } catch {
      setEstado("denegado");
    }
  }

  const screenClass =
    estado === "buscando"
      ? "sc-scan-screen sc-scan-screen--searching"
      : "sc-scan-screen";

  return (
    <div className={screenClass}>
      <div className="sc-scan-header">
        <button
          type="button"
          className="sc-scan-close"
          onClick={() => {
            port?.detener();
            goHome(false);
          }}
        >
          <Icon name="x" size={21} stroke={2} />
        </button>
        <div className="sc-scan-title">Escanear código</div>
      </div>

      {/* Un solo `<video>` persistente (nunca condicional): montarlo y
          desmontarlo entre estados perdería el `srcObject` que `iniciar()`
          ya haya fijado. Se oculta con `display:none` en vez de sacarlo del
          árbol — el nodo y su stream sobreviven al cambio de estado. */}
      <div
        className="sc-scan-body"
        style={{ display: estado === "buscando" ? undefined : "none" }}
      >
        <div className="sc-scan-frame">
          <video ref={videoRef} muted playsInline />
          <span className="sc-scan-corner sc-scan-corner--tl" />
          <span className="sc-scan-corner sc-scan-corner--tr" />
          <span className="sc-scan-corner sc-scan-corner--bl" />
          <span className="sc-scan-corner sc-scan-corner--br" />
          <span className="sc-scan-line" />
          {invalidNotice ? (
            <div className="sc-scan-invalid">
              <Notice>{invalidNotice}</Notice>
            </div>
          ) : null}
        </div>
        <div>
          <div className="sc-scan-heading">Buscando código…</div>
          <div className="sc-scan-copy">
            Encuadra el QR de la app del miembro dentro del marco. Se lee solo,
            no hay que tocar nada.
          </div>
        </div>
        <button
          type="button"
          className="sc-scan-help"
          onClick={() => {
            port?.detener();
            goHome(true);
          }}
        >
          ¿La cámara no funciona?
        </button>
      </div>

      <div
        className="sc-scan-body"
        style={{ display: estado === "permiso" ? undefined : "none" }}
      >
        <div className="sc-scan-circle">
          <Icon name="camera" size={36} color="var(--text2)" stroke={1.5} />
        </div>
        <div>
          <div className="sc-scan-permission-title">Permiso de cámara</div>
          <div className="sc-scan-permission-copy">
            El navegador te pedirá acceso a la cámara. Toca{" "}
            <b style={{ color: "var(--text)" }}>Permitir</b> para poder leer los
            códigos QR.
          </div>
        </div>
        <div className="sc-scan-actions">
          <BigButton
            tone="green"
            onClick={() => {
              void startCamera();
            }}
          >
            Permitir cámara
          </BigButton>
          <div className="sc-scan-gap" />
          <BigButton
            tone="ghost"
            onClick={() => {
              goHome(false);
            }}
          >
            Ahora no
          </BigButton>
        </div>
      </div>

      <div
        className="sc-scan-denied"
        style={{ display: estado === "denegado" ? undefined : "none" }}
      >
        <div className="sc-scan-denied-inner">
          <div className="sc-scan-circle sc-scan-circle--danger">
            <Icon name="camera" size={30} color="var(--danger)" stroke={1.6} />
          </div>
          <div className="sc-scan-denied-title">La cámara está bloqueada</div>
          <div className="sc-scan-denied-copy">
            Para habilitarla en este celular:
          </div>
          <div className="sc-scan-steps">
            {DENIED_STEPS.map((step, index) => (
              <div key={step} className="sc-scan-step">
                <span className="sc-scan-step-index">{index + 1}</span>
                <span className="sc-scan-step-text">{step}</span>
              </div>
            ))}
          </div>
          <BigButton
            tone="green"
            onClick={() => {
              void startCamera();
            }}
          >
            <Icon name="refresh" size={17} stroke={1.9} />
            Reintentar
          </BigButton>
          <div className="sc-scan-gap" />
          <BigButton
            tone="ghost"
            onClick={() => {
              goHome(true);
            }}
          >
            Ingresar el código a mano
          </BigButton>
        </div>
      </div>
    </div>
  );
}
